/*eslint-disable react-hooks/exhaustive-deps*/
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Row, Col, Table, Button, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter, Input, Label,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setLoadingOverlay } from '../../store/slices/loadingOverlaySlice';
import NoRecords from '../../components/Table/NoRecords';
import profitabilityService from '../../services/profitability';
import LocalstorageService from '../../helpers/localstorage-service';
import { CompanyId } from '../../constants';

const LocationGroups = () => {
  const [setPageTitle, setCrumbItem] = useOutletContext();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const companyId = LocalstorageService.getItem(CompanyId);

  const [groups, setGroups] = useState([]);

  // מודל עריכה / יצירה
  const [modalOpen, setModalOpen]   = useState(false);
  const [editGroup, setEditGroup]   = useState(null);
  const [form, setForm]             = useState({ groupName: '', groupCode: '', displayOrder: 0, checkForName: '', nameOnInvoice: '' });

  // מודל מחיקה
  const [deleteGroup, setDeleteGroup] = useState(null);

  // מודל שיוך מסופים
  const [assignModal, setAssignModal]         = useState(false);
  const [assignGroup, setAssignGroup]         = useState(null);
  const [allTerminals, setAllTerminals]       = useState([]);
  const [selectedTerminals, setSelectedTerminals] = useState(new Set());
  const [terminalSearch, setTerminalSearch]   = useState('');

  useEffect(() => {
    setPageTitle(t('PROFITABILITY.LOCATION_GROUPS_PAGE.PAGE_TITLE'));
    setCrumbItem([
      { title: t('PROFITABILITY.LOCATION_GROUPS_PAGE.CRUMB_PARENT'), link: '#' },
      { title: t('PROFITABILITY.LOCATION_GROUPS_PAGE.CRUMB_ITEM'), link: '#' },
    ]);
  }, []);

  const load = useCallback(async () => {
    dispatch(setLoadingOverlay(true));
    try {
      const res = await profitabilityService.getLocationGroups({ companyId });
      setGroups(res?.data?.dataList || []);
    } catch {
      toast.error(t('PROFITABILITY.LOCATION_GROUPS_PAGE.TOAST_ERROR_LOAD'));
    } finally {
      dispatch(setLoadingOverlay(false));
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // ── עריכה / יצירה ──────────────────────────────────────
  const openAdd = () => {
    setEditGroup(null);
    setForm({ groupName: '', groupCode: '', displayOrder: (groups.length + 1) * 10, checkForName: '', nameOnInvoice: '' });
    setModalOpen(true);
  };

  const openEdit = (g) => {
    setEditGroup(g);
    setForm({
      groupName:    g.groupName,
      groupCode:    g.groupCode    || '',
      displayOrder: g.displayOrder || 0,
      checkForName: g.checkForName || '',
      nameOnInvoice: g.nameOnInvoice || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.groupName.trim()) { toast.warning(t('PROFITABILITY.LOCATION_GROUPS_PAGE.TOAST_WARN_NAME')); return; }
    dispatch(setLoadingOverlay(true));
    try {
      await profitabilityService.saveLocationGroup({
        action: editGroup ? 'UPDATE' : 'INSERT',
        id: editGroup?.id || null,
        companyId,
        groupName:     form.groupName.trim(),
        groupCode:     form.groupCode.trim()     || null,
        displayOrder:  parseInt(form.displayOrder) || 0,
        checkForName:  form.checkForName.trim()  || null,
        nameOnInvoice: form.nameOnInvoice.trim() || null,
      });
      toast.success(editGroup ? t('PROFITABILITY.LOCATION_GROUPS_PAGE.TOAST_SAVED') : t('PROFITABILITY.LOCATION_GROUPS_PAGE.TOAST_ADDED'));
      setModalOpen(false);
      load();
    } catch {
      toast.error(t('PROFITABILITY.LOCATION_GROUPS_PAGE.TOAST_ERROR_SAVE'));
    } finally {
      dispatch(setLoadingOverlay(false));
    }
  };

  // ── מחיקה ──────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteGroup) return;
    dispatch(setLoadingOverlay(true));
    try {
      await profitabilityService.saveLocationGroup({
        action: 'DELETE',
        id: deleteGroup.id,
        companyId,
        groupName: deleteGroup.groupName,
      });
      toast.success(t('PROFITABILITY.LOCATION_GROUPS_PAGE.TOAST_DELETED', { name: deleteGroup.groupName }));
      setDeleteGroup(null);
      load();
    } catch {
      toast.error(t('PROFITABILITY.LOCATION_GROUPS_PAGE.TOAST_ERROR_DELETE'));
    } finally {
      dispatch(setLoadingOverlay(false));
    }
  };

  // ── שיוך מסופים ────────────────────────────────────────
  const openAssign = async (g) => {
    setAssignGroup(g);
    setTerminalSearch('');
    dispatch(setLoadingOverlay(true));
    try {
      const res = await profitabilityService.getTerminalsForAssignment({ companyId });
      const terminals = res?.data?.dataList || [];
      setAllTerminals(terminals);
      const preSelected = new Set(
        terminals.filter(t => t.locationGroupId === g.id).map(t => t.terminalNumber)
      );
      setSelectedTerminals(preSelected);
      setAssignModal(true);
    } catch {
      toast.error(t('PROFITABILITY.LOCATION_GROUPS_PAGE.TOAST_ERROR_TERMINALS'));
    } finally {
      dispatch(setLoadingOverlay(false));
    }
  };

  const toggleTerminal = (terminalNumber) => {
    setSelectedTerminals(prev => {
      const next = new Set(prev);
      if (next.has(terminalNumber)) next.delete(terminalNumber);
      else next.add(terminalNumber);
      return next;
    });
  };

  const handleAssignSave = async () => {
    dispatch(setLoadingOverlay(true));
    try {
      await profitabilityService.assignGroupTerminals({
        groupId: assignGroup.id,
        companyId,
        terminalNumbers: Array.from(selectedTerminals).join(','),
      });
      toast.success(t('PROFITABILITY.LOCATION_GROUPS_PAGE.TOAST_ASSIGNED', { name: assignGroup.groupName, count: selectedTerminals.size }));
      setAssignModal(false);
      load();
    } catch {
      toast.error(t('PROFITABILITY.LOCATION_GROUPS_PAGE.TOAST_ERROR_ASSIGN'));
    } finally {
      dispatch(setLoadingOverlay(false));
    }
  };

  const filteredTerminals = allTerminals.filter(t =>
    !terminalSearch ||
    (t.locationName || '').toLowerCase().includes(terminalSearch.toLowerCase()) ||
    (t.terminalNumber || '').includes(terminalSearch)
  );

  // ── Render ──────────────────────────────────────────────
  return (
    <React.Fragment>
      <Row className="mb-3 align-items-center">
        <Col>
          <span className="text-muted small">{t('PROFITABILITY.LOCATION_GROUPS_PAGE.GROUPS_COUNT', { count: groups.length })}</span>
        </Col>
        <Col xs="auto">
          <Button color="primary" size="sm" onClick={openAdd}>
            <i className="ri-add-line me-1"></i>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.NEW_GROUP')}
          </Button>
        </Col>
      </Row>

      {groups.length === 0 ? <NoRecords /> : (
        <div className="table-responsive">
          <Table bordered hover size="sm">
            <thead className="table-dark">
              <tr>
                <th>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.TABLE.GROUP_NAME')}</th>
                <th>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.TABLE.CODE')}</th>
                <th>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.TABLE.CHECK_NAME')}</th>
                <th>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.TABLE.INVOICE_NAME')}</th>
                <th className="text-center">{t('PROFITABILITY.LOCATION_GROUPS_PAGE.TABLE.TERMINALS')}</th>
                <th className="text-center">{t('PROFITABILITY.LOCATION_GROUPS_PAGE.TABLE.ORDER')}</th>
                <th className="text-center" style={{ width: 100 }}>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.TABLE.ACTIONS')}</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => (
                <tr key={g.id}>
                  <td className="fw-bold">
                    <i className="ri-map-pin-2-line me-1 text-primary"></i>
                    {g.groupName}
                  </td>
                  <td><code className="text-muted small">{g.groupCode || '—'}</code></td>
                  <td className="small">{g.checkForName || <span className="text-muted">—</span>}</td>
                  <td className="small">{g.nameOnInvoice || <span className="text-muted">—</span>}</td>
                  <td className="text-center">
                    <Badge color="info" pill>{g.terminalCount || 0}</Badge>
                  </td>
                  <td className="text-center text-muted small">{g.displayOrder}</td>
                  <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                    {/* שיוך מסופים */}
                    <Button color="success" size="sm" className="me-1 py-0 px-1"
                      title={t('PROFITABILITY.LOCATION_GROUPS_PAGE.ACTION.ASSIGN_TERMINALS')} onClick={() => openAssign(g)}>
                      <i className="ri-links-line"></i>
                    </Button>
                    {/* עריכה */}
                    <Button color="warning" size="sm" className="me-1 py-0 px-1"
                      title={t('PROFITABILITY.LOCATION_GROUPS_PAGE.ACTION.EDIT')} onClick={() => openEdit(g)}>
                      <i className="ri-edit-line"></i>
                    </Button>
                    {/* מחיקה */}
                    <Button color="danger" size="sm" className="py-0 px-1"
                      title={t('PROFITABILITY.LOCATION_GROUPS_PAGE.ACTION.DELETE')} onClick={() => setDeleteGroup(g)}>
                      <i className="ri-delete-bin-line"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* ── מודל יצירה / עריכה ──────────────────────────── */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} dir="rtl">
        <ModalHeader toggle={() => setModalOpen(false)}>
          {editGroup ? t('PROFITABILITY.LOCATION_GROUPS_PAGE.EDIT_MODAL.TITLE_EDIT') : t('PROFITABILITY.LOCATION_GROUPS_PAGE.EDIT_MODAL.TITLE_NEW')}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.EDIT_MODAL.GROUP_NAME_LABEL')}</Label>
            <Input
              value={form.groupName}
              onChange={e => setForm(p => ({ ...p, groupName: e.target.value }))}
              placeholder={t('PROFITABILITY.LOCATION_GROUPS_PAGE.EDIT_MODAL.GROUP_NAME_PLACEHOLDER')}
            />
          </div>
          <div className="mb-3">
            <Label>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.EDIT_MODAL.CODE_LABEL')}</Label>
            <Input
              value={form.groupCode}
              onChange={e => setForm(p => ({ ...p, groupCode: e.target.value }))}
              placeholder={t('PROFITABILITY.LOCATION_GROUPS_PAGE.EDIT_MODAL.CODE_PLACEHOLDER')}
            />
          </div>
          <div className="mb-3">
            <Label>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.EDIT_MODAL.ORDER_LABEL')}</Label>
            <Input
              type="number"
              value={form.displayOrder}
              onChange={e => setForm(p => ({ ...p, displayOrder: e.target.value }))}
              placeholder={t('PROFITABILITY.LOCATION_GROUPS_PAGE.EDIT_MODAL.ORDER_PLACEHOLDER')}
            />
          </div>
          <div className="mb-3">
            <Label>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.EDIT_MODAL.CHECK_NAME_LABEL')}</Label>
            <Input
              value={form.checkForName}
              onChange={e => setForm(p => ({ ...p, checkForName: e.target.value }))}
              placeholder={t('PROFITABILITY.LOCATION_GROUPS_PAGE.EDIT_MODAL.CHECK_NAME_PLACEHOLDER')}
            />
            <small className="text-muted">{t('PROFITABILITY.LOCATION_GROUPS_PAGE.EDIT_MODAL.CHECK_NAME_HELP')}</small>
          </div>
          <div className="mb-3">
            <Label>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.EDIT_MODAL.INVOICE_NAME_LABEL')}</Label>
            <Input
              value={form.nameOnInvoice}
              onChange={e => setForm(p => ({ ...p, nameOnInvoice: e.target.value }))}
              placeholder={t('PROFITABILITY.LOCATION_GROUPS_PAGE.EDIT_MODAL.INVOICE_NAME_PLACEHOLDER')}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSave}>
            <i className="ri-save-line me-1"></i>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.EDIT_MODAL.SAVE_BUTTON')}
          </Button>
          <Button color="secondary" outline onClick={() => setModalOpen(false)}>
            {t('PROFITABILITY.LOCATION_GROUPS_PAGE.EDIT_MODAL.CANCEL_BUTTON')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── מודל אישור מחיקה ────────────────────────────── */}
      <Modal isOpen={!!deleteGroup} toggle={() => setDeleteGroup(null)} dir="rtl" size="sm">
        <ModalHeader toggle={() => setDeleteGroup(null)}>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.DELETE_MODAL.TITLE')}</ModalHeader>
        <ModalBody>
          <p>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.DELETE_MODAL.MESSAGE')} <strong>{deleteGroup?.groupName}</strong>?</p>
          {deleteGroup?.terminalCount > 0 && (
            <p className="text-warning small">
              <i className="ri-alert-line me-1"></i>
              {t('PROFITABILITY.LOCATION_GROUPS_PAGE.DELETE_MODAL.TERMINALS_WARNING', { count: deleteGroup.terminalCount })}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleDelete}>
            <i className="ri-delete-bin-line me-1"></i>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.DELETE_MODAL.DELETE_BUTTON')}
          </Button>
          <Button color="secondary" outline onClick={() => setDeleteGroup(null)}>
            {t('PROFITABILITY.LOCATION_GROUPS_PAGE.DELETE_MODAL.CANCEL_BUTTON')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── מודל שיוך מסופים ─────────────────────────────── */}
      <Modal isOpen={assignModal} toggle={() => setAssignModal(false)} dir="rtl" size="lg">
        <ModalHeader toggle={() => setAssignModal(false)}>
          {t('PROFITABILITY.LOCATION_GROUPS_PAGE.ASSIGN_MODAL.TITLE')} — <strong>{assignGroup?.groupName}</strong>
        </ModalHeader>
        <ModalBody>
          <Input
            className="mb-2"
            placeholder={t('PROFITABILITY.LOCATION_GROUPS_PAGE.ASSIGN_MODAL.SEARCH_PLACEHOLDER')}
            value={terminalSearch}
            onChange={e => setTerminalSearch(e.target.value)}
          />
          <div className="mb-2 d-flex justify-content-between align-items-center">
            <span className="text-muted small">
              {t('PROFITABILITY.LOCATION_GROUPS_PAGE.ASSIGN_MODAL.SELECTED_COUNT', { selected: selectedTerminals.size, total: allTerminals.length })}
            </span>
            <div>
              <Button size="sm" color="link" className="p-0 me-2"
                onClick={() => setSelectedTerminals(new Set(filteredTerminals.map(t => t.terminalNumber)))}>
                {t('PROFITABILITY.LOCATION_GROUPS_PAGE.ASSIGN_MODAL.SELECT_ALL')}
              </Button>
              <Button size="sm" color="link" className="p-0 text-muted"
                onClick={() => setSelectedTerminals(new Set())}>
                {t('PROFITABILITY.LOCATION_GROUPS_PAGE.ASSIGN_MODAL.CLEAR_ALL')}
              </Button>
            </div>
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            <Table bordered size="sm" className="mb-0">
              <thead className="table-light sticky-top">
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.ASSIGN_MODAL.TABLE.TERMINAL')}</th>
                  <th>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.ASSIGN_MODAL.TABLE.LOCATION')}</th>
                  <th>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.ASSIGN_MODAL.TABLE.CURRENT_GROUP')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredTerminals.map(term => {
                  const checked      = selectedTerminals.has(term.terminalNumber);
                  const isOtherGroup = term.locationGroupId && term.locationGroupId !== assignGroup?.id;
                  return (
                    <tr key={term.terminalNumber}
                      className={checked ? 'table-success' : ''}
                      style={{ cursor: 'pointer' }}
                      onMouseDown={() => toggleTerminal(term.terminalNumber)}>
                      <td className="text-center">
                        <input type="checkbox" checked={checked} readOnly />
                      </td>
                      <td><code className="small">{term.terminalNumber}</code></td>
                      <td>{term.locationName}</td>
                      <td className="small">
                        {isOtherGroup
                          ? <span className="text-warning">{term.groupName}</span>
                          : checked
                            ? <span className="text-success">{t('PROFITABILITY.LOCATION_GROUPS_PAGE.ASSIGN_MODAL.THIS_GROUP')}</span>
                            : <span className="text-muted">—</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAssignSave}>
            <i className="ri-save-line me-1"></i>{t('PROFITABILITY.LOCATION_GROUPS_PAGE.ASSIGN_MODAL.SAVE_BUTTON', { count: selectedTerminals.size })}
          </Button>
          <Button color="secondary" outline onClick={() => setAssignModal(false)}>
            {t('PROFITABILITY.LOCATION_GROUPS_PAGE.ASSIGN_MODAL.CANCEL_BUTTON')}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default LocationGroups;
